import React, { useEffect, useState } from "react";
import { StudyMaterial, StudyMaterialFile } from "../../../../types/dataModel";
import { getAPIHeaders, getAPIMultiPartHeaders } from "../../../../common/Utils";
import useFetch from "../../../../hooks/useFetch";
import { API } from "../../../../common/Constants";
import FormFooter from "../../../../core/common/formFooter";
import PreLoader from "../../../../core/common/preLoader";
import CustomToast from "../../../../core/common/customToast";

interface Props {
  syllabusId: number;
  studyMaterial?: StudyMaterial | null;
  nodeData: any;
  isPublic: boolean;
  onIsPublicChange: (value: boolean) => void;
  onSuccess: () => void;
  onCancel: () => void;
}

const FileMaterialFormSection: React.FC<Props> = ({
  syllabusId,
  studyMaterial,
  nodeData,
  isPublic,
  onIsPublicChange,
  onSuccess,
  onCancel,
}) => {
  const [title, setTitle] = useState<string>("");
  const [selectedFiles, setSelectedFiles] = useState<StudyMaterialFile[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [editingFileIndex, setEditingFileIndex] = useState<number | null>(null);
  const [editingFileName, setEditingFileName] = useState<string>("");
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
    show: false,
    message: "Something went wrong please try again later",
    heading: "Oops! Something went wrong",
    type: "ERROR",
  });

  const { isLoading, error, callFetch } = useFetch();

  useEffect(() => {
    if (error?.response?.data) {
      setToastSettings((prev) => ({
        ...prev,
        show: true,
        message: error.response?.data || "Something went wrong, please try again later.",
        heading: "Error",
        type: "ERROR",
      }));
    }
  }, [error]);

  useEffect(() => {
    if (studyMaterial) {
      setTitle(studyMaterial.title || "");
      setSelectedFiles(studyMaterial.files || []);
    } else {
      setTitle("");
      setSelectedFiles([]);
    }
    setUploadProgress(0);
    setIsUploading(false);
    setErrors({});
    setEditingFileIndex(null);
    setEditingFileName("");
  }, [studyMaterial]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!title?.trim()) {
      newErrors.title = "Title is required";
    }

    if (selectedFiles.length === 0 && !studyMaterial) {
      newErrors.files = "Please select at least one file";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []) as StudyMaterialFile[];
    if (!files.length) {
      return;
    }
    setSelectedFiles((prev) => [...prev, ...files]);
    setErrors((prev) => ({ ...prev, files: "" }));
    event.target.value = "";
  };

  const handleEditFileName = (file: StudyMaterialFile, index: number) => {
    const fileName = file.fileName || file.name || "";
    setEditingFileIndex(index);
    setEditingFileName(fileName);
  };

  const handleSaveFileName = (index: number) => {
    setSelectedFiles((prev) =>
      prev.map((file, i) => {
        if (i === index) {
          const updatedFile = { ...file };
          updatedFile.fileName = editingFileName.trim() || file.name || "";
          return updatedFile;
        }
        return file;
      })
    );
    setEditingFileIndex(null);
    setEditingFileName("");
  };

  const handleCancelEditFileName = () => {
    setEditingFileIndex(null);
    setEditingFileName("");
  };

  const removeFile = (file: StudyMaterialFile, index: number) => {
    if (file.id) {
      handleDeleteFile(file.id, index);
    } else {
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleDeleteFile = async (fileJunctionId: number, index: number) => {
    try {
      await callFetch(
        API.REMOVE_FILE_FROM_STUDY_MATERIAL.replace(":fileId", fileJunctionId.toString()),
        "DELETE",
        getAPIHeaders(),
        null,
        null
      );
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error("Error deleting file:", error);
    }
  };

  const handleAddFilesToMaterial = async () => {
    if (studyMaterial?.id && selectedFiles.length) {
      const newFiles = selectedFiles.filter((file) => !file.id);
      if (!newFiles.length) {
        return Promise.resolve();
      }

      const formDataToSend = new FormData();
      newFiles.forEach((file) => {
        formDataToSend.append("files", file);
      });
      formDataToSend.append("displayOrder", (selectedFiles.length + 1).toString());

      setIsUploading(true);
      setUploadProgress(0);

      return callFetch(
        API.ADD_FILES_TO_STUDY_MATERIAL.replace(":id", studyMaterial.id.toString()),
        "POST",
        getAPIMultiPartHeaders(),
        null,
        formDataToSend,
        (progressEvent: any) => {
          if (progressEvent.percentCompleted !== undefined) {
            setUploadProgress(progressEvent.percentCompleted);
          }
        }
      )
        .finally(() => {
          setIsUploading(false);
          setUploadProgress(0);
        });
    }
    return Promise.resolve();
  };

  const updateStudyMaterial = async () => {
    if (!studyMaterial?.id) {
      return;
    }
    
    // Collect updated fileNames for existing files
    const filesToUpdate = selectedFiles
      .filter((file) => file.id)
      .map((file) => ({
        id: file.id,
        fileId: file.fileId,
        file: {fileName: file.fileName || ""},
      }));

    const updatePayload: any = {
      title: title,
      content: studyMaterial.content || "",
      materialType: "FILE",
      isPublic: isPublic || false,
    };

    // Include files with updated fileName if any
    if (filesToUpdate.length > 0) {
      updatePayload.studyMaterialFiles = filesToUpdate;
    }

    await callFetch(
      API.UPDATE_STUDY_MATERIAL.replace(":id", studyMaterial.id.toString()),
      "PUT",
      getAPIHeaders(),
      null,
      updatePayload
    );
  };

  const createStudyMaterialWithFiles = async () => {
    if (selectedFiles.length === 0) {
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("title", title);
    formDataToSend.append("content", "");
    formDataToSend.append("isPublic", (isPublic || false).toString());
    formDataToSend.append("materialType", "FILE");

    if (syllabusId) formDataToSend.append("syllabusId", syllabusId.toString());
    if (nodeData?.nodeType === "UNIT") formDataToSend.append("unitId", nodeData?.id?.toString() || "");
    if (nodeData?.nodeType === "SECTION") formDataToSend.append("sectionId", nodeData?.id?.toString() || "");

    selectedFiles.forEach((file) => {
      formDataToSend.append("files", file);
    });

    setIsUploading(true);
    setUploadProgress(0);

    await callFetch(
      API.ADD_STUDY_MATERIAL_MULTIPLE_FILES,
      "POST",
      getAPIMultiPartHeaders(),
      null,
      formDataToSend,
      (progressEvent: any) => {
        if (progressEvent.percentCompleted !== undefined) {
          setUploadProgress(progressEvent.percentCompleted);
        }
      }
    ).finally(() => {
      setIsUploading(false);
      setUploadProgress(0);
    });
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (studyMaterial?.id) {
        await updateStudyMaterial();
        await handleAddFilesToMaterial();
        onSuccess();
      } else {
        await createStudyMaterialWithFiles();
        onSuccess();
      }
    } catch (submissionError) {
      console.error("Error saving file study material:", submissionError);
    }
  };

  return (
    <>
      <PreLoader showLoader={isLoading || isUploading} />
      <CustomToast
        show={toastSettings.show}
        message={toastSettings.message}
        heading={toastSettings.heading}
        type={toastSettings.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Title</label>
          <input
            type="text"
            className={`form-control ${errors.title ? "is-invalid" : ""}`}
            placeholder="Enter material title..."
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (errors.title) setErrors((prev) => ({ ...prev, title: "" }));
            }}
          />
          {errors.title && <div className="invalid-feedback">{errors.title}</div>}
        </div>

        <div className="mb-3">
          <label className="form-label">Select Files</label>
          <input
            type="file"
            className={`form-control ${errors.files ? "is-invalid" : ""}`}
            multiple
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
            disabled={isUploading}
          />
          {errors.files && <div className="invalid-feedback">{errors.files}</div>}
          <div className="form-text">Supported formats: PDF, DOC, PPT, XLS, TXT, Images (Max 50MB per file)</div>

          {selectedFiles.length > 0 && (
            <div className="mt-3">
              <h6 className="mb-2">Selected Files:</h6>
              <div className="d-flex flex-wrap gap-2">
                {selectedFiles.map((file, index) => {
                  const isEditing = editingFileIndex === index;
                  const fileName = file.fileName || file.name || "";
                  const isExistingFile = !!file.id;

                  return (
                    <div key={`${file.name || file.fileName}-${index}`} className="d-flex flex-column bg-light p-2 rounded w-100">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="flex-grow-1 d-flex flex-column">
                          {isEditing ? (
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={editingFileName}
                              onChange={(e) => setEditingFileName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleSaveFileName(index);
                                } else if (e.key === "Escape") {
                                  handleCancelEditFileName();
                                }
                              }}
                              autoFocus
                            />
                          ) : (
                            <div className="fs-12 fw-medium">
                              <i className="ti ti-file" /> {fileName}
                            </div>
                          )}
                          {file.size && <div className="fs-11 text-muted">Size: {(file.size / 1024 / 1024).toFixed(2)} MB</div>}
                        </div>
                        {!isUploading && (
                          <div className="d-flex gap-1 ms-2">
                            {isExistingFile && !isEditing && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleEditFileName(file, index)}
                                title="Edit file name"
                              >
                                <i className="ti ti-edit" />
                              </button>
                            )}
                            {isEditing && (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => handleSaveFileName(index)}
                                  title="Save"
                                >
                                  <i className="ti ti-check" />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={handleCancelEditFileName}
                                  title="Cancel"
                                >
                                  <i className="ti ti-x" />
                                </button>
                              </>
                            )}
                            {!isEditing && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => removeFile(file, index)}
                                title="Delete"
                              >
                                <i className="ti ti-trash" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {isUploading && index === 0 && uploadProgress > 0 && (
                        <div className="mt-2">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="fs-11 text-muted">Uploading...</span>
                            <span className="fs-11 fw-medium">{uploadProgress}%</span>
                          </div>
                          <div className="progress" style={{ height: "8px" }}>
                            <div
                              className="progress-bar bg-primary"
                              role="progressbar"
                              style={{ width: `${uploadProgress}%` }}
                              aria-valuenow={uploadProgress}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="mb-3">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="fileIsPublic"
              checked={isPublic}
              onChange={(e) => onIsPublicChange(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="fileIsPublic">
              Make this material public
            </label>
          </div>
          <div className="form-text">Public materials are visible to all students. Private materials are only visible to faculty.</div>
        </div>

        <FormFooter onCancel={onCancel} onSave={handleSubmit} readonly={isLoading || isUploading} noBootstrap={true} />
      </form>
    </>
  );
};

export default FileMaterialFormSection;

